/**
 * Restitution du matching d'UN candidat contre le référentiel d'une cible.
 *
 * Rendue sous le classement de l'onglet Matching de la campagne, et dans la
 * fiche candidat (onglet du test › Matching). En fit-culturel c'est le rapport
 * de matching culturel complet ; en PAMI la lecture en cible & écart.
 */
import { useMemo, type ReactNode } from "react";
import { useQuery } from "convex/react";
import { AlertTriangle, CheckCircle2, Search } from "lucide-react";
import { Badge, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import { PamiMatchingChart, type LigneMatchingPami } from "@repo/results";
import { dateCourte } from "@repo/utils";
import { api } from "../../../../convex/_generated/api";
import type { StatDimension } from "../../../../convex/referentiel/agregation";
import type { LigneComparaison } from "../../../../convex/referentiel/comparer";
import { construireRapportCulturel } from "../../../../convex/referentiel/rapport";
import { RapportMatchingCulturel } from "./RapportMatchingCulturel";
import type {
  EntreeMatching,
  MatchingReferentiel,
} from "./useMatchingReferentiel";

// Diverging blue ↔ red avec point neutre gris : les deux pôles se lisent comme
// opposés (sous la cible / au-dessus de la cible), le milieu comme « rien ».
// Jamais la couleur seule — chaque ligne porte aussi son libellé de statut.
const COULEUR_STATUT: Record<LigneComparaison["statut"], string> = {
  "sous-la-cible": "var(--chart-region-2)",
  "au-dessus-de-la-cible": "var(--chart-region-4)",
  "dans-la-cible": "var(--muted-foreground)",
  // Jamais atteint dans le tableau fit-culturel (Q-sort n'a pas de dimension
  // « sans zone ») — présent pour l'exhaustivité du type, partagé avec le PAMI.
  "sans-zone": "var(--muted-foreground)",
};

const LIBELLE_STATUT: Record<LigneComparaison["statut"], string> = {
  "sous-la-cible": "Sous la cible",
  "au-dessus-de-la-cible": "Au-dessus de la cible",
  "dans-la-cible": "Dans la cible",
  "sans-zone": "Pas d'attente particulière",
};

const arrondi1 = (n: number) => Math.round(n * 10) / 10;

/**
 * Bande à tracer pour une ligne, quelle que soit l'échelle.
 *
 * En fourchette la bande EST la cible saisie. En Q-sort c'est le CRAN attendu :
 * `comparerQsort` ne tolère plus d'écart, la bande est donc le demi-cran de part
 * et d'autre du niveau du référentiel — ce qui fait que « marqueur dans la
 * bande » signifie exactement « même niveau », sans pouvoir contredire le statut.
 *
 * Un demi-cran de chaque côté et non `[niveau − 1, niveau]` comme les anneaux du
 * radar : sur une échelle linéaire, un niveau est un POINT, et une bande partant
 * du cran inférieur ferait tomber un candidat un cran trop bas pile sur son bord.
 */
function bandeCible(ligne: LigneComparaison): { min: number; max: number } {
  if (ligne.cible.kind === "fourchette") {
    return { min: ligne.cible.min, max: ligne.cible.max };
  }
  if (ligne.cible.kind === "score") {
    return { min: ligne.cible.valeur - 0.5, max: ligne.cible.valeur + 0.5 };
  }
  // "sans-zone" : jamais produit par `comparerQsort`, seule cible utilisée
  // dans cette table (fit-culturel). Le PAMI passe par `PamiMatchingChart`.
  return { min: ligne.valeurCandidat, max: ligne.valeurCandidat };
}

/**
 * Bande cible dessinée sur l'échelle, marqueur du candidat par-dessus.
 *
 * Sert les deux échelles : en fourchette la bande EST la cible `[min, max]` sur
 * 0-10 ; en Q-sort c'est le cran attendu par la pyramide finale, à un demi-cran
 * près, tracé sur l'échelle des scores de niveau. Dans les deux cas, « le
 * marqueur est dans la bande » signifie exactement « dans la cible ».
 */
function TrackCible({
  bande,
  valeurCandidat,
  echelle,
  statut,
}: {
  bande: { min: number; max: number };
  valeurCandidat: number;
  echelle: { min: number; max: number };
  statut: LigneComparaison["statut"];
}) {
  const amplitude = echelle.max - echelle.min || 1;
  const pct = (v: number) =>
    Math.max(0, Math.min(100, ((v - echelle.min) / amplitude) * 100));
  return (
    <div className="relative h-4 w-full min-w-[7rem] rounded-full bg-muted">
      <div
        className="absolute inset-y-0 rounded-full bg-foreground/15"
        style={{
          left: `${pct(bande.min)}%`,
          width: `${Math.max(1.5, pct(bande.max) - pct(bande.min))}%`,
        }}
      />
      {/* Marqueur candidat : cerné de 2px de surface pour rester lisible
          par-dessus la bande cible. */}
      <div
        className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-card"
        style={{
          left: `${pct(valeurCandidat)}%`,
          backgroundColor: COULEUR_STATUT[statut],
        }}
      />
    </div>
  );
}

/** Barre divergente centrée sur 0 : à gauche sous la cible, à droite au-dessus. */
function BarreEcart({
  ligne,
  ecartMaxAffiche,
}: {
  ligne: LigneComparaison;
  ecartMaxAffiche: number;
}) {
  const largeur =
    ecartMaxAffiche > 0 ? (Math.abs(ligne.ecart) / ecartMaxAffiche) * 50 : 0;
  const versLaGauche = ligne.statut === "sous-la-cible";
  return (
    <div className="relative h-4 w-full min-w-[6rem]">
      <div className="absolute inset-y-0 left-1/2 w-px bg-border" />
      {ligne.statut === "dans-la-cible" ? (
        <div className="absolute top-1/2 left-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-muted-foreground" />
      ) : (
        <div
          className="absolute inset-y-[3px] rounded-full"
          style={{
            [versLaGauche ? "right" : "left"]: "50%",
            width: `${Math.max(1, largeur)}%`,
            backgroundColor: COULEUR_STATUT[ligne.statut],
          }}
        />
      )}
    </div>
  );
}

/**
 * Score PAMI sur 100. Pas de palier d'interprétation : le matching y est le
 * décompte des niveaux atteints, une grandeur qui se lit telle quelle. Le
 * fit-culturel (r) se lit dans `RapportMatchingCulturel`.
 */
function JaugeMatching({
  score,
  fiabilite,
  rang,
  total,
}: {
  score: number;
  fiabilite: "faible" | "correcte";
  rang: number;
  total: number;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-end gap-3">
        <span className="text-4xl leading-none font-bold tabular-nums">
          {Math.round(score)}
        </span>
        <span className="text-muted-foreground pb-1 text-sm">/ 100</span>
        {fiabilite === "faible" && (
          <Badge variant="outline" className="mb-1">
            Fiabilité faible
          </Badge>
        )}
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary"
          style={{ width: `${Math.max(1, Math.min(100, score))}%` }}
        />
      </div>
      <p className="text-muted-foreground text-xs">
        Rang {rang} sur {total} dans cette campagne.
      </p>
    </div>
  );
}

function BlocDimensions({
  titre,
  description,
  icone,
  lignes,
  noteHexaco,
}: {
  titre: string;
  description: string;
  icone: ReactNode;
  lignes: LigneComparaison[];
  noteHexaco?: boolean;
}) {
  const aDesFacettes =
    noteHexaco && lignes.some((l) => l.categorie === "personnalite");
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {icone}
          {titre}
          <span className="text-muted-foreground text-sm font-normal">
            ({lignes.length})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-muted-foreground text-xs">{description}</p>
        {lignes.length === 0 ? (
          <p className="text-muted-foreground">Aucune dimension.</p>
        ) : (
          <ul className="space-y-1">
            {lignes.map((ligne) => (
              <li key={ligne.code} className="flex items-baseline gap-2">
                <span className="font-medium">{ligne.label}</span>
                {ligne.categorie === "personnalite" && (
                  <span className="text-muted-foreground text-xs">
                    · à explorer en entretien
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {aDesFacettes && (
          <p className="text-muted-foreground border-t pt-2 text-xs">
            Les facettes de personnalité décrivent un attendu du poste, pas une
            aptitude : un écart signale un point à explorer en entretien, il ne
            qualifie pas le candidat.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

type Props = {
  /** Matching de la campagne pour cette cible — version, échelle, classement. */
  matching: MatchingReferentiel;
  /** Le candidat restitué, une entrée de `matching.classement`. */
  entree: EntreeMatching;
  /** Tient lieu de poste visé dans le rapport de matching culturel. */
  campagneNom?: string;
  /**
   * Jeton d'auth et URL du déploiement de l'app appelante, pour proposer le
   * rapport PDF remis au candidat. Absents = pas de bouton : ce paquet ne lit
   * aucun `process.env`, qui serait vide hors du bundle de l'app.
   */
  token?: string | null;
  convexUrl?: string;
};

export function RestitutionMatchingReferentiel({
  matching,
  entree,
  campagneNom,
  token,
  convexUrl,
}: Props) {
  const { etat, version, echelle, pyramide, niveauxForces, classement } =
    matching;
  const selection = entree;

  // Rapports du candidat sélectionné (candidat, recruteur…), pour le menu
  // posé dans l'en-tête de sa restitution.
  const rapportsDisponibles = useQuery(
    api.reports.rapportsDisponibles,
    convexUrl ? { evaluationId: selection.row._id } : "skip",
  );

  /**
   * Rapport de matching culturel du candidat sélectionné. Fit-culturel
   * UNIQUEMENT : la corrélation r suppose deux vecteurs ipsatifs comparables
   * item par item, ce qu'une cible en fourchette n'est pas. Le PAMI garde donc
   * la restitution en cible & écart plus bas.
   */
  const rapportCulturel = useMemo(() => {
    if (!etat || !version || !pyramide || !selection.vecteurCandidat) {
      return null;
    }
    return construireRapportCulturel({
      dimensions: etat.config.dimensions,
      vecteurCandidat: selection.vecteurCandidat,
      agregat: version.dimensions as Record<string, StatDimension>,
      pyramide,
      nbReferents: version.nbReferents,
      niveauxForces,
    });
  }, [etat, version, pyramide, niveauxForces, selection.vecteurCandidat]);

  /**
   * Lignes prêtes pour le graphique de matching PAMI. Les cibles en score moyen
   * ± dispersion (Q-sort) n'y entrent jamais — cette figure est PAMI only. Une
   * dimension « sans zone » (pas d'attente particulière) y entre quand même : pas d'anneau
   * `[min,max]` à dessiner, mais le score du candidat doit rester visible.
   */
  const lignesMatchingPami: LigneMatchingPami[] = useMemo(
    () =>
      (selection.comparaison?.parDimension ?? []).flatMap(
        (ligne): LigneMatchingPami[] => {
          if (ligne.cible.kind === "fourchette") {
            return [
              {
                code: ligne.code,
                label: ligne.label,
                valeurCandidat: ligne.valeurCandidat,
                cible: {
                  min: ligne.cible.min,
                  max: ligne.cible.max,
                  poids: ligne.cible.poids,
                },
                ecart: ligne.ecart,
                statut: ligne.statut,
              },
            ];
          }
          if (ligne.cible.kind === "sans-zone") {
            return [
              {
                code: ligne.code,
                label: ligne.label,
                valeurCandidat: ligne.valeurCandidat,
                cible: { sansZone: true as const },
                ecart: ligne.ecart,
                statut: ligne.statut,
              },
            ];
          }
          return [];
        },
      ),
    [selection.comparaison],
  );

  if (!etat || !version) return null;

  const neutralisees = new Set(version.dimensionsNeutralisees);
  const enDesaccord = new Set(version.dimensionsEnDesaccord);
  // Échelle de tracé de la cible : 0-10 en fourchette, scores de niveau en Q-sort
  // (le dernier niveau vaut 1, le premier vaut `nbNiveaux` — cf. `scoreDuNiveau`).
  const echelleTrace = echelle ?? { min: 1, max: pyramide?.length ?? 1 };
  const ecartMaxAffiche = Math.max(
    1,
    ...(selection.comparaison?.parDimension.map((l) => Math.abs(l.ecart)) ?? [
      1,
    ]),
  );
  const position =
    classement.findIndex((c) => c.row.parcoursId === selection.row.parcoursId) +
    1;

  return (
    <>
      {/* Restitution du candidat sélectionné. En fit-culturel c'est le rapport
          de matching culturel complet ; en PAMI la lecture en cible & écart. */}
      {rapportCulturel ? (
        <RapportMatchingCulturel
          rapport={rapportCulturel}
          candidat={`${selection.row.prenomCandidat ?? ""} ${
            selection.row.nomCandidat ?? ""
          }`.trim()}
          campagneNom={campagneNom}
          datePassation={
            "dateFin" in selection.row ? selection.row.dateFin : undefined
          }
          reference={{
            version: version.version,
            computedAt: version.computedAt,
            nbReferents: version.nbReferents,
          }}
          rapportPdf={
            convexUrl
              ? {
                  evaluationId: selection.row._id,
                  prenom: selection.row.prenomCandidat,
                  nom: selection.row.nomCandidat,
                  rapports: rapportsDisponibles,
                  token: token ?? null,
                  convexUrl,
                }
              : undefined
          }
          rang={
            selection.comparaison?.scoreMatching === null ||
            selection.comparaison === null
              ? undefined
              : {
                  position,
                  total: classement.length,
                }
          }
        />
      ) : selection.comparaison ? (
        <>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex flex-wrap items-center gap-3 text-base">
                {selection.row.prenomCandidat} {selection.row.nomCandidat}
                <Badge variant="info">
                  v{version.version} · {version.nbReferents} référent
                  {version.nbReferents > 1 ? "s" : ""} ·{" "}
                  {dateCourte(version.computedAt)}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {selection.comparaison.scoreMatching === null ? (
                <p className="text-sm">
                  {echelle
                    ? "Score non défini : aucune dimension de ce référentiel ne discrimine. Le référent doit se positionner sur au moins une dimension pour qu'un score soit calculable."
                    : "Score non défini : aucune dimension classée par les référents ne se retrouve dans la passation de ce candidat."}
                </p>
              ) : (
                <JaugeMatching
                  score={selection.comparaison.scoreMatching}
                  fiabilite={selection.comparaison.fiabilite}
                  rang={position}
                  total={classement.length}
                />
              )}

              {/* PAMI : une SEULE figure remplace le détail ligne à ligne —
                  les zones du référentiel en anneaux polaires, le profil du
                  candidat en radar par-dessus. Le tableau reste la restitution
                  de repli du fit culturel, dont la cible est un score entouré
                  d'un cran de pyramide et non une plage saisie. */}
              {echelle ? (
                <PamiMatchingChart
                  lignes={lignesMatchingPami}
                  echelle={echelle}
                />
              ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/30 border-b">
                      <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                        Dimension
                      </th>
                      <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                        Cible &amp; candidat
                      </th>
                      <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                        Candidat
                      </th>
                      <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                        Écart
                      </th>
                      <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                        Statut
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selection.comparaison.parDimension.map((ligne) => (
                      <tr key={ligne.code} className="border-b last:border-0">
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap items-baseline gap-2">
                            <span className="font-medium">{ligne.label}</span>
                            {ligne.categorie === "personnalite" && (
                              <span className="text-muted-foreground text-xs">
                                personnalité
                              </span>
                            )}
                            {/* En PAMI, toutes les zones à attente unique sont
                                étroites : le badge serait sur chaque ligne. */}
                            {ligne.exigenceForte &&
                              ligne.cible.kind !== "fourchette" && (
                                <Badge variant="outline">exigence forte</Badge>
                              )}
                            {neutralisees.has(ligne.code) && (
                              <span className="text-muted-foreground text-xs">
                                hors calcul
                              </span>
                            )}
                            {enDesaccord.has(ligne.code) && (
                              <span className="text-muted-foreground text-xs">
                                référents en désaccord
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <TrackCible
                            bande={bandeCible(ligne)}
                            valeurCandidat={ligne.valeurCandidat}
                            echelle={echelleTrace}
                            statut={ligne.statut}
                          />
                          <span className="text-muted-foreground text-xs">
                            {ligne.cible.kind === "fourchette"
                              ? `cible ${arrondi1(ligne.cible.min)}–${arrondi1(ligne.cible.max)}`
                              : ligne.cible.kind === "score"
                                ? `attendu ${arrondi1(ligne.cible.valeur)} ± ${arrondi1(
                                    Math.max(1, ligne.cible.dispersion),
                                  )}`
                                : "—"}
                          </span>
                        </td>
                        <td className="px-3 py-2 tabular-nums">
                          {arrondi1(ligne.valeurCandidat)}
                        </td>
                        <td className="px-3 py-2">
                          <BarreEcart
                            ligne={ligne}
                            ecartMaxAffiche={ecartMaxAffiche}
                          />
                          <span className="text-muted-foreground text-xs tabular-nums">
                            {ligne.ecart === 0
                              ? "0"
                              : `${arrondi1(Math.abs(ligne.ecart))} pt${
                                  Math.abs(ligne.ecart) > 1 ? "s" : ""
                                }`}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className="flex items-center gap-1.5 whitespace-nowrap">
                            <span
                              className="h-2 w-2 shrink-0 rounded-full"
                              style={{
                                backgroundColor: COULEUR_STATUT[ligne.statut],
                              }}
                            />
                            {LIBELLE_STATUT[ligne.statut]}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </CardContent>
          </Card>

          {!echelle && (
          <div className="grid gap-4 md:grid-cols-3">
            <BlocDimensions
              titre="Points forts"
              description="Dans la cible, sur des dimensions qui pèsent — zone attendue en PAMI, attente tranchée en PC Fit."
              icone={<CheckCircle2 className="h-4 w-4" />}
              lignes={selection.comparaison.pointsForts}
            />
            <BlocDimensions
              titre="Points de vigilance"
              description="Sous la cible sur une dimension qui pèse fortement."
              icone={<AlertTriangle className="h-4 w-4" />}
              lignes={selection.comparaison.pointsVigilance}
              noteHexaco
            />
            <BlocDimensions
              titre="Angles morts"
              description="Au-dessus de la cible : des attentes du candidat que le poste ne sert pas."
              icone={<Search className="h-4 w-4" />}
              lignes={selection.comparaison.anglesMorts}
              noteHexaco
            />
          </div>
          )}
        </>
      ) : null}
    </>
  );
}
